import { api } from './apiClient';

/**
 * Coach Profile API Service
 * Handles all coach-related API calls
 */

// Coach Profile Management
export const coachAPI = {
  // Get list of coaches with filtering
  getCoaches: async (params = {}) => {
    const response = await api.get('/profiles/coaches/', { params });
    return response.data;
  },

  // Get specific coach profile
  getCoachProfile: async (coachId) => {
    const response = await api.get(`/profiles/coaches/${coachId}/`);
    return response.data;
  },

  // Get my coach profile (authenticated coach)
  getMyProfile: async () => {
    const response = await api.get('/profiles/coaches/me/');
    return response.data;
  },

  // Get public coach profile by ID or slug
  getPublicProfile: async (idOrSlug) => {
    const response = await api.get(`/profiles/coaches/${idOrSlug}/public_profile/`);
    return response.data;
  },

  // Create coach profile
  createProfile: async (profileData) => {
    const response = await api.post('/profiles/coaches/', profileData);
    return response.data;
  },

  // Update coach profile
  updateProfile: async (coachId, profileData) => {
    const response = await api.patch(`/profiles/coaches/${coachId}/`, profileData);
    return response.data;
  },

  // Get onboarding status
  getOnboardingStatus: async () => {
    const response = await api.get('/profiles/coaches/onboarding_status/');
    return response.data;
  },

  // Upload profile image
  uploadProfileImage: async (coachId, imageFile) => {
    const formData = new FormData();
    formData.append('profile_image', imageFile);
    
    const response = await api.patch(`/profiles/coaches/${coachId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Upload cover image
  uploadCoverImage: async (coachId, imageFile) => {
    const formData = new FormData();
    formData.append('cover_image', imageFile);
    
    const response = await api.patch(`/profiles/coaches/${coachId}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Coach Onboarding Workflow
export const coachOnboardingAPI = {
  // Start onboarding
  startOnboarding: async (coachType) => {
    const response = await api.post('/profiles/coach/onboarding/start/', { 
      coach_type: coachType 
    });
    return response.data;
  },

  // Update basic info (Step 1)
  updateBasicInfo: async (data) => {
    const response = await api.post('/profiles/coach/onboarding/basic-info/', data);
    return response.data;
  },

  // Update professional info (Step 2)
  updateProfessionalInfo: async (data) => {
    const response = await api.post('/profiles/coach/onboarding/professional-info/', data);
    return response.data;
  },

  // Update business setup (Step 3)
  updateBusinessSetup: async (data) => {
    const response = await api.post('/profiles/coach/onboarding/business-setup/', data);
    return response.data;
  },

  // Setup availability (Step 4)
  setupAvailability: async (data) => {
    const response = await api.post('/profiles/coach/onboarding/availability/', data);
    return response.data;
  },

  // Setup pricing (Step 5)
  setupPricing: async (data) => {
    const response = await api.post('/profiles/coach/onboarding/pricing/', data);
    return response.data;
  },

  // Setup marketing (Step 6 - Optional)
  setupMarketing: async (data) => {
    const response = await api.post('/profiles/coach/onboarding/marketing/', data);
    return response.data;
  },

  // Complete onboarding
  completeOnboarding: async () => {
    const response = await api.post('/profiles/coach/onboarding/complete/');
    return response.data;
  },

  // Get onboarding progress
  getProgress: async () => {
    const response = await api.get('/profiles/coach/onboarding/progress/');
    return response.data;
  },
};

// Coach Availability Management
export const coachAvailabilityAPI = {
  // Get all availability settings
  getAvailabilityList: async () => {
    const response = await api.get('/profiles/coach-availability/');
    return response.data;
  },

  // Get specific availability
  getAvailability: async (availabilityId) => {
    const response = await api.get(`/profiles/coach-availability/${availabilityId}/`);
    return response.data;
  },

  // Get my availability
  getMyAvailability: async () => {
    const response = await api.get('/profiles/coach-availability/my_availability/');
    return response.data;
  },

  // Create availability
  createAvailability: async (data) => {
    const response = await api.post('/profiles/coach-availability/', data);
    return response.data;
  },

  // Update availability
  updateAvailability: async (availabilityId, data) => {
    const response = await api.patch(`/profiles/coach-availability/${availabilityId}/`, data);
    return response.data;
  },

  // Add vacation dates
  addVacation: async (availabilityId, vacationData) => {
    const response = await api.post(
      `/profiles/coach-availability/${availabilityId}/add_vacation/`, 
      vacationData
    );
    return response.data;
  },

  // Update working hours
  updateWorkingHours: async (availabilityId, workingHours) => {
    const response = await api.post(
      `/profiles/coach-availability/${availabilityId}/update_working_hours/`, 
      { working_hours: workingHours }
    );
    return response.data;
  },
};

// Search and Filter Helpers
export const coachSearchParams = {
  // Build search parameters for coach listing
  buildSearchParams: (filters) => {
    const params = {};
    
    if (filters.coachType) {
      params.coach_type = filters.coachType;
    }
    
    if (filters.timezone) {
      params.timezone = filters.timezone;
    }
    
    if (filters.language) {
      params.language = filters.language;
    }
    
    if (filters.specialization) {
      params.specialization = filters.specialization;
    }
    
    if (filters.minRating) {
      params.min_rating = filters.minRating;
    }
    
    if (filters.acceptingClients !== undefined) {
      params.accepting_clients = filters.acceptingClients;
    }
    
    if (filters.search) {
      params.search = filters.search;
    }
    
    if (filters.ordering) {
      params.ordering = filters.ordering;
    }
    
    return params;
  },
};

// Export all APIs
export default {
  coach: coachAPI,
  onboarding: coachOnboardingAPI,
  availability: coachAvailabilityAPI,
  searchParams: coachSearchParams,
};